import { useEffect, useState } from 'react';
import { openTickerChart } from '../tickerChart';
import { InfoTip } from './InfoTip';
import { PANEL_INFO } from '../panelInfo';

interface BtcData {
  updatedAt: string;
  price: {
    usd: number;
    change24h: number | null;
    change7d: number | null;
    change30d: number | null;
    high24h: number | null;
    low24h: number | null;
    volume24h: number | null;
    marketCap: number | null;
    sparkline7d: number[];
  } | null;
  dominance: number | null;
  fearGreed: { value: number; label: string; yesterday: number | null; lastWeek: number | null } | null;
  derivatives: {
    fundingRate: number | null;
    fundingAnnualized: number | null;
    openInterestBtc: number | null;
    openInterestUsd: number | null;
    source: string;
  } | null;
  vsMarket: Array<{ ticker: string; changePercent: number | null }>;
  onChain: {
    fastestFee: number | null;
    halfHourFee: number | null;
    hourFee: number | null;
    mempoolTxs: number | null;
    hashrateEh: number | null;
    difficultyChangePct: number | null;
    retargetDays: number | null;
  } | null;
  halving: {
    blocksLeft: number;
    estDays: number;
    estDate: string;
    currentReward: number;
    nextReward: number;
  } | null;
  supply: {
    circulating: number | null;
    percentMined: number | null;
    ath: number | null;
    athChangePct: number | null;
    athDate: string | null;
  } | null;
  headlines: Array<{ title: string; url: string; source: string }>;
  gold: {
    usd: number;
    change24h: number | null;
    change7d: number | null;
    change30d: number | null;
    ozPerBtc: number | null;
  } | null;
}

const chg = (v: number | null) => (v === null ? 'var(--text-dim)' : v >= 0 ? 'var(--green)' : 'var(--red)');
const pct = (v: number | null, d = 1) => (v === null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}%`);

function compact(n: number): string {
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${n.toFixed(0)}`;
}

function fgColor(v: number): string {
  if (v <= 25) return 'var(--red)';
  if (v <= 45) return 'var(--amber)';
  if (v <= 55) return 'var(--text-dim)';
  return 'var(--green)';
}

function sparkPath(prices: number[], w = 100, h = 32): string {
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const span = max - min || 1;
  return (
    'M' +
    prices
      .map((p, i) => {
        const x = (i / (prices.length - 1)) * w;
        const y = h - 2 - ((p - min) / span) * (h - 4);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' L')
  );
}

/**
 * Rates → BTC: the Fed's expected rate path (from /api/fed, cached server-side)
 * translated into what it has historically meant for bitcoin. Educational
 * framing — mechanism in the tooltip, no predictions.
 */
function RatesBtcCard() {
  const [exp, setExp] = useState<{
    mood: 'dovish' | 'neutral' | 'hawkish' | null;
    impliedBp12mo: number | null;
    effective: number | null;
  } | null>(null);

  useEffect(() => {
    fetch('/api/fed')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d?.expectations && setExp(d.expectations))
      .catch(() => {});
  }, []);

  if (!exp || !exp.mood) return null;

  const read = {
    dovish: { label: 'TAILWIND', color: 'var(--green)', text: 'cuts priced — easier money has historically favored BTC' },
    neutral: { label: 'NEUTRAL', color: 'var(--text-dim)', text: 'market expects the Fed to hold — rates not the driver right now' },
    hawkish: { label: 'HEADWIND', color: 'var(--amber)', text: 'hikes priced — tighter money has historically pressured BTC' },
  }[exp.mood];

  return (
    <div className="btc-card card-with-tip">
      <h3 className="btc-card-title">
        Rates → BTC
        <InfoTip
          paragraphs={[
            'Why rates move bitcoin: when the Fed raises rates, cash and bonds pay more, the dollar strengthens, and leverage gets expensive — money rotates out of assets that yield nothing, like BTC. Rate cuts do the reverse: cheaper money, weaker dollar, more liquidity chasing risk. BTC’s biggest runs came in easy-money eras (2020–21, post-2024 cuts); its worst year (2022, −65%) was the fastest hiking cycle in decades.',
            'The link is strong but not mechanical: halvings, adoption waves and ETF flows can overwhelm it, and BTC sometimes trades as a debasement hedge — rallying on the very deficits and money-printing that rate policy responds to. This card reads the market-implied rate path (see the Fed section) and labels the historical relationship, not a prediction.',
          ]}
        />
      </h3>
      <div className="btc-price" style={{ color: read.color }}>{read.label}</div>
      <div className="btc-rows">
        <span>
          Fed path:{' '}
          <b style={{ color: read.color }}>
            {exp.mood.toUpperCase()}
            {exp.impliedBp12mo !== null &&
              ` (${exp.impliedBp12mo >= 0 ? '+' : ''}${Math.round(exp.impliedBp12mo)}bp/12mo)`}
          </b>
        </span>
      </div>
      <div className="btc-rows btc-dim">
        <span>{read.text}</span>
      </div>
    </div>
  );
}

export function BitcoinSection() {
  const [data, setData] = useState<BtcData | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch('/api/bitcoin');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setData(await r.json());
        setError(false);
      } catch {
        setError(true);
      }
    };
    load();
    const interval = setInterval(load, 180000);
    return () => clearInterval(interval);
  }, []);

  if (error && !data) return null; // hide section entirely if the API is dead
  const p = data?.price ?? null;
  const fg = data?.fearGreed ?? null;
  const dv = data?.derivatives ?? null;

  return (
    <div className="panel bitcoin-section" id="bitcoin">
      <div className="panel-header">
        <div className="panel-title">
          <h2>₿ Bitcoin</h2>
          <InfoTip paragraphs={PANEL_INFO.bitcoin} />
        </div>
        <span className="badge">
          {data?.dominance !== null && data?.dominance !== undefined
            ? `${data.dominance.toFixed(1)}% dominance`
            : 'Live'}
        </span>
      </div>

      {!data ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="btc-grid">
          {/* Price & Momentum */}
          {p && (
            <div
              className="btc-card clickable-row"
              title="Click for BTCUSD chart"
              onClick={() => openTickerChart('BTCUSD')}
            >
              <h3 className="btc-card-title">Price & Momentum</h3>
              <div className="btc-price">
                ${p.usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                <span style={{ color: chg(p.change24h), fontSize: 14, marginLeft: 8 }}>
                  {pct(p.change24h)}
                </span>
              </div>
              {p.sparkline7d.length > 3 && (
                <svg className="btc-spark" viewBox="0 0 100 32" preserveAspectRatio="none">
                  <path
                    d={sparkPath(p.sparkline7d)}
                    fill="none"
                    stroke={
                      (p.change7d ?? 0) >= 0 ? 'var(--green-mid)' : 'var(--red)'
                    }
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              )}
              <div className="btc-rows">
                <span>7d <b style={{ color: chg(p.change7d) }}>{pct(p.change7d)}</b></span>
                <span>30d <b style={{ color: chg(p.change30d) }}>{pct(p.change30d)}</b></span>
              </div>
              <div className="btc-rows btc-dim">
                <span>
                  24h {p.low24h !== null ? `$${(p.low24h / 1000).toFixed(1)}k` : '—'}–
                  {p.high24h !== null ? `$${(p.high24h / 1000).toFixed(1)}k` : '—'}
                </span>
                <span>vol {p.volume24h !== null ? compact(p.volume24h) : '—'}</span>
              </div>
            </div>
          )}

          {/* Fear & Greed */}
          {fg && (
            <div className="btc-card">
              <h3 className="btc-card-title">Fear & Greed</h3>
              <div className="btc-fg" style={{ color: fgColor(fg.value) }}>
                {fg.value}
              </div>
              <div className="btc-fg-label" style={{ color: fgColor(fg.value) }}>
                {fg.label}
              </div>
              <div className="btc-fg-track">
                <div className="btc-fg-dot" style={{ left: `${fg.value}%` }} />
              </div>
              <div className="btc-rows btc-dim">
                <span>yday {fg.yesterday ?? '—'}</span>
                <span>1w ago {fg.lastWeek ?? '—'}</span>
              </div>
            </div>
          )}

          {/* Leverage & Derivatives */}
          {dv && dv.fundingRate !== null && (
            <div className="btc-card">
              <h3 className="btc-card-title">Leverage (perps)</h3>
              <div className="btc-price" style={{ color: chg(dv.fundingRate) }}>
                {(dv.fundingRate * 100).toFixed(4)}%
                <span className="btc-dim" style={{ fontSize: 11, marginLeft: 6 }}>8h funding</span>
              </div>
              <div className="btc-rows">
                <span>
                  annualized{' '}
                  <b style={{ color: chg(dv.fundingAnnualized) }}>
                    {pct(dv.fundingAnnualized !== null ? dv.fundingAnnualized * 100 : null)}
                  </b>
                </span>
              </div>
              <div className="btc-rows btc-dim">
                <span>
                  {dv.fundingRate >= 0 ? 'longs paying shorts' : 'shorts paying longs'} —{' '}
                  {Math.abs(dv.fundingRate) > 0.0003 ? 'crowded' : 'balanced'}
                </span>
              </div>
              {dv.openInterestUsd !== null && (
                <div className="btc-rows btc-dim">
                  <span>open interest {compact(dv.openInterestUsd)}</span>
                </div>
              )}
            </div>
          )}

          {/* BTC vs Market */}
          {p && data.vsMarket.length > 0 && (
            <div className="btc-card">
              <h3 className="btc-card-title">BTC vs Market</h3>
              <div className="btc-vs">
                {[{ ticker: 'BTC', changePercent: p.change24h }, ...data.vsMarket].map((r) => (
                  <div className="btc-vs-row" key={r.ticker}>
                    <span className="btc-vs-ticker">{r.ticker}</span>
                    <div className="btc-vs-track">
                      {r.changePercent !== null && (
                        <div
                          className="btc-vs-bar"
                          style={{
                            width: `${Math.min(Math.abs(r.changePercent) * 8, 50)}%`,
                            [r.changePercent >= 0 ? 'left' : 'right']: '50%',
                            background: r.changePercent >= 0 ? 'var(--green-mid)' : 'var(--red)',
                          }}
                        />
                      )}
                    </div>
                    <span style={{ color: chg(r.changePercent), fontWeight: 700 }}>
                      {pct(r.changePercent, 2)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="btc-rows btc-dim">
                <span>
                  {p.change24h !== null && data.vsMarket[0]?.changePercent !== null
                    ? Math.sign(p.change24h) === Math.sign(data.vsMarket[0].changePercent as number)
                      ? 'moving with equities'
                      : 'diverging from equities'
                    : ''}
                </span>
              </div>
            </div>
          )}

          {/* On-Chain Pulse */}
          {data.onChain && (
            <div className="btc-card">
              <h3 className="btc-card-title">On-Chain Pulse</h3>
              {data.onChain.fastestFee !== null && (
                <div className="btc-rows">
                  <span>
                    fees <b>{data.onChain.fastestFee}</b>
                    <span className="btc-dim"> fast</span> · <b>{data.onChain.halfHourFee ?? '—'}</b>
                    <span className="btc-dim"> 30m</span> · <b>{data.onChain.hourFee ?? '—'}</b>
                    <span className="btc-dim"> 1h sat/vB</span>
                  </span>
                </div>
              )}
              <div className="btc-rows">
                <span>
                  mempool{' '}
                  <b>
                    {data.onChain.mempoolTxs !== null
                      ? `${(data.onChain.mempoolTxs / 1000).toFixed(1)}k txs`
                      : '—'}
                  </b>
                </span>
                <span>
                  hashrate{' '}
                  <b>
                    {data.onChain.hashrateEh !== null
                      ? `${data.onChain.hashrateEh.toFixed(0)} EH/s`
                      : '—'}
                  </b>
                </span>
              </div>
              {data.onChain.difficultyChangePct !== null && (
                <div className="btc-rows btc-dim">
                  <span>
                    difficulty{' '}
                    <b style={{ color: chg(data.onChain.difficultyChangePct) }}>
                      {pct(data.onChain.difficultyChangePct)}
                    </b>{' '}
                    in {data.onChain.retargetDays !== null ? `${data.onChain.retargetDays.toFixed(1)}d` : '—'}
                  </span>
                </div>
              )}
              <div className="btc-rows btc-dim">
                <span>
                  {data.onChain.fastestFee !== null
                    ? data.onChain.fastestFee <= 5
                      ? 'chain is quiet — cheap to transact'
                      : data.onChain.fastestFee <= 30
                        ? 'moderate activity'
                        : 'chain is busy — fees elevated'
                    : ''}
                </span>
              </div>
            </div>
          )}

          {/* Supply, Cycle & Halving — one card, one story */}
          {(data.supply?.circulating != null || data.halving) && (
            <div className="btc-card">
              <h3 className="btc-card-title">Supply & Halving</h3>
              {data.supply?.circulating != null && (
                <>
                  <div className="btc-rows">
                    <span>
                      mined <b>{(data.supply.circulating / 1e6).toFixed(2)}M</b>
                      <span className="btc-dim"> / 21M</span>
                    </span>
                    <span>
                      <b>{data.supply.percentMined?.toFixed(1)}%</b>
                    </span>
                  </div>
                  <div className="btc-fg-track" style={{ background: 'var(--green-faint)', opacity: 1 }}>
                    <div
                      className="btc-supply-fill"
                      style={{ width: `${data.supply.percentMined ?? 0}%` }}
                    />
                  </div>
                </>
              )}
              {data.supply?.ath != null && (
                <div className="btc-rows">
                  <span>
                    ATH <b>${data.supply.ath.toLocaleString('en-US', { maximumFractionDigits: 0 })}</b>
                    <span className="btc-dim">
                      {' '}
                      {data.supply.athDate
                        ? new Date(data.supply.athDate).toLocaleDateString('en-US', {
                            month: 'short',
                            year: 'numeric',
                          })
                        : ''}
                    </span>
                  </span>
                  <span style={{ color: chg(data.supply.athChangePct), fontWeight: 700 }}>
                    {pct(data.supply.athChangePct)}
                  </span>
                </div>
              )}
              {data.halving && (
                <div className="btc-rows btc-dim">
                  <span>
                    halving ~<b>{Math.round(data.halving.estDays)}d</b> (
                    {new Date(data.halving.estDate).toLocaleDateString('en-US', {
                      month: 'short',
                      year: 'numeric',
                    })}
                    ) · reward {data.halving.currentReward} → {data.halving.nextReward}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* BTC vs Gold */}
          {data.gold && (
            <div className="btc-card">
              <h3 className="btc-card-title">BTC vs Gold</h3>
              <div className="btc-rows">
                <span>
                  gold{' '}
                  <b>${data.gold.usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}</b>
                  <span className="btc-dim">/oz</span>
                </span>
                <span style={{ color: chg(data.gold.change24h), fontWeight: 700 }}>
                  {pct(data.gold.change24h)}
                </span>
              </div>
              {data.gold.ozPerBtc !== null && (
                <div className="btc-price" style={{ fontSize: 18 }}>
                  1 BTC = {data.gold.ozPerBtc.toFixed(1)} oz
                </div>
              )}
              {p && (
                <>
                  <div className="btc-rows">
                    <span className="btc-dim">7d</span>
                    <span>
                      BTC <b style={{ color: chg(p.change7d) }}>{pct(p.change7d)}</b>
                      {' · '}gold{' '}
                      <b style={{ color: chg(data.gold.change7d) }}>{pct(data.gold.change7d)}</b>
                    </span>
                  </div>
                  <div className="btc-rows">
                    <span className="btc-dim">30d</span>
                    <span>
                      BTC <b style={{ color: chg(p.change30d) }}>{pct(p.change30d)}</b>
                      {' · '}gold{' '}
                      <b style={{ color: chg(data.gold.change30d) }}>{pct(data.gold.change30d)}</b>
                    </span>
                  </div>
                  {p.change30d !== null && data.gold.change30d !== null && (
                    <div className="btc-rows btc-dim">
                      <span>
                        {p.change30d > data.gold.change30d
                          ? 'BTC outpacing gold this month'
                          : 'gold outpacing BTC this month'}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Rates → BTC (bridges the Fed section) */}
          <RatesBtcCard />

          {/* Headlines */}
          <div className="btc-card btc-card-news">
            <h3 className="btc-card-title">BTC Headlines</h3>
            {data.headlines.length === 0 ? (
              <div className="btc-dim" style={{ fontSize: 12 }}>Headlines unavailable right now</div>
            ) : (
              data.headlines.map((n, i) => (
                <a key={i} className="btc-headline" href={n.url} target="_blank" rel="noreferrer">
                  {n.title}
                  <span className="btc-dim"> — {n.source}</span>
                </a>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
